// useSaveNudge — fires a one-shot toast after N minutes of unsaved
// activity to remind unauthenticated users that creating an account
// would persist their work. Conversion-critical for free-tier users.
//
// Suppression: localStorage flag so a single browser session doesn't
// nag repeatedly. The flag resets on save / sign-in.

import { useEffect, useRef } from 'react';
import { toast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';

const NUDGE_FIRED_KEY = 'fw.onboarding.saveNudgeFiredAt';
const NUDGE_DELAY_MS = 5 * 60 * 1_000; // 5 minutes
const NUDGE_COOLDOWN_MS = 24 * 60 * 60 * 1_000; // 1 day between nudges

export interface SaveNudgeOptions {
  /** When true, treat the workspace as dirty so the timer counts. */
  isDirty: boolean;
  /** Called when the user clicks the "Skapa konto"-button in the toast. */
  onSignUp?: () => void;
}

/**
 * Mounts a single timer that fires the toast once per 24h, only when
 * the user is signed-OUT and the canvas is dirty.
 */
export function useSaveNudge({ isDirty, onSignUp }: SaveNudgeOptions): void {
  const { isAuthenticated } = useAuth();
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    // Clear any existing timer when state changes.
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    if (isAuthenticated || !isDirty) return;

    // Respect cooldown.
    try {
      const lastRaw = localStorage.getItem(NUDGE_FIRED_KEY);
      const last = lastRaw ? Number(lastRaw) : 0;
      if (last > 0 && Date.now() - last < NUDGE_COOLDOWN_MS) return;
    } catch {
      /* storage unavailable */
    }

    timerRef.current = window.setTimeout(() => {
      try {
        localStorage.setItem(NUDGE_FIRED_KEY, String(Date.now()));
      } catch {
        /* ignore */
      }
      toast({
        title: 'Spara ditt arbete',
        description:
          'Skapa ett konto för att synka workflows mellan enheter — annars försvinner det när du stänger fliken.',
        action: onSignUp
          ? {
              altText: 'Skapa konto',
              label: 'Skapa konto',
              onClick: onSignUp,
            } as unknown as React.ReactElement
          : undefined,
      });
    }, NUDGE_DELAY_MS);

    return () => {
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isAuthenticated, isDirty, onSignUp]);
}

/** Reset cooldown so the next dirty-edit triggers a fresh timer. */
export function resetSaveNudgeCooldown(): void {
  try {
    localStorage.removeItem(NUDGE_FIRED_KEY);
  } catch {
    /* ignore */
  }
}
